import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, Navigate, Link, useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import './App.css';
import PrivacyPolicy from './components/PrivacyPolicy';
import AdminPanel from './components/AdminPanel';
import GiftLanding from './components/GiftLanding';
import GiftRedeemPage from './components/GiftRedeemPage';
import GiftFortuneModal from './components/GiftFortuneModal';
import GiftHistoryTab from './components/GiftHistoryTab';
import GrowthHubLayout from './components/growth-hub/GrowthHubLayout';
import EspressolabFinderDemo from './components/EspressolabFinderDemo';
import { getPartnerAttribution } from './components/GiftLanding';

// PHASE 3 NEW MODULES
import CompatibilityTab from './components/CompatibilityTab';
import NatalChartTab from './components/NatalChartTab';
import HybridAnalysisTab from './components/HybridAnalysisTab';
import AICoachTab from './components/AICoachTab';

// Rewarded Ads
import AdsAnalytics from './components/AdsAnalytics';
import WatchAdButton from './components/WatchAdButton';

// Country Analytics
import CountryAnalytics from './components/CountryAnalytics';

// Analytics Service
import { initializeAnalytics, trackLoginSuccess, trackSignupSuccess, trackTabViewed } from './services/analytics';

// Oracle Feature
import { OracleLandingPage, OracleNewPage, OracleReadingPage } from './features/oracle';

// Palm Oracle Feature (v2.5.0)
import { PalmLandingPage, PalmNewPage, PalmReadingPage, PalmAccessGuard } from './features/palm';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============ GOOGLE AUTH CALLBACK ============
const AuthCallback = ({ onSuccess }) => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          setStatus('error');
          setError('Session bilgisi bulunamadı');
          return;
        }

        const { data } = await axios.post(`${API_URL}/api/auth/google-callback`, {
          session_id: sessionId
        });

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        if (onSuccess) onSuccess(data.user);
        setStatus('success');

        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } catch (err) {
        console.error('Google auth callback error:', err);
        setStatus('error');
        setError(err.response?.data?.detail || 'Google giriş başarısız oldu');
      }
    };

    processCallback();
  }, [onSuccess]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)',
      fontFamily: 'inherit'
    }}>
      <div style={{
        textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.05)',
        borderRadius: '16px', maxWidth: '400px', width: '90%'
      }}>
        {status === 'processing' && (
          <>
            <div style={{ fontSize: '40px', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }}>🔮</div>
            <h2 style={{ color: 'white', margin: '0 0 8px' }}>Google ile giriş yapılıyor...</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0 }}>Lütfen bekleyin</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
            <h2 style={{ color: '#4ade80', margin: '0 0 8px' }}>Giriş Başarılı!</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0 }}>Yönlendiriliyorsunuz...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>❌</div>
            <h2 style={{ color: '#f87171', margin: '0 0 8px' }}>Giriş Başarısız</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 20px' }}>{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px', background: '#8b5cf6', border: 'none',
                borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px'
              }}
            >
              Ana Sayfaya Dön
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ============ AUTH ERROR HANDLER ============
// List of auth error codes that should trigger logout
const AUTH_ERROR_CODES = [
  'AUTH_ERROR_NO_CREDENTIALS',
  'AUTH_ERROR_INVALID_SCHEME',
  'AUTH_ERROR_TOKEN_MISSING',
  'AUTH_ERROR_TOKEN_MALFORMED',
  'AUTH_ERROR_TOKEN_EXPIRED',
  'AUTH_ERROR_SIGNATURE_INVALID',
  'AUTH_ERROR_DECODE_FAILED',
  'AUTH_ERROR_USER_NOT_FOUND',
  'AUTH_ERROR_NO_USER_ID'
];

// Global axios interceptor for auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const errorDetail = error.response?.data?.detail || '';
      console.error('🔒 AUTH ERROR:', errorDetail);
      
      // Check if it's an auth-related error
      const isAuthError = AUTH_ERROR_CODES.some(code => errorDetail.includes(code)) ||
                          errorDetail === 'Token expired' ||
                          errorDetail === 'Invalid token' ||
                          errorDetail === 'User not found';
      
      if (isAuthError) {
        console.warn('🚪 AUTO LOGOUT: Clearing storage and redirecting to login');
        
        // Clear all auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.clear();
        
        // Redirect to login (use window.location to force full reload)
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/?auth=login&reason=' + encodeURIComponent(errorDetail);
        }
      }
    }
    return Promise.reject(error);
  }
);

// ============ COMPONENTS ============

// Platform Detection - Native mi Web mi?
const isNativePlatform = () => {
  // Capacitor native platform check
  if (typeof window !== 'undefined' && window.Capacitor) {
    return window.Capacitor.isNativePlatform();
  }
  return false;
};

// Hero/Landing Page
const Hero = ({ onShowAuth, onLogin }) => {
  const { t, i18n } = useTranslation();
  const isNative = isNativePlatform();
  
  const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.mysticai.app";
  
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'tr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    console.log('Language changed to:', newLang);
    window.location.reload(); // Force reload to apply language change
  };

  return (
    <div className="hero-container">
      {/* Google Play Download Banner - SADECE WEB'DE GÖSTERİLİR */}
      {!isNative && (
        <div className="app-download-banner">
          <div className="banner-content">
            <span className="banner-text">
              📱 {i18n.language === 'tr' ? 'Mobil Uygulamayı İndir!' : 'Download the Mobile App!'}
            </span>
            <a 
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="google-play-btn"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Google Play'den İndir"
                style={{ height: '40px' }}
              />
            </a>
          </div>
        </div>
      )}

      <nav className="hero-nav">
        <div className="logo">
          {/* Native'de sadece icon, Web'de tam logo */}
          <img 
            src={isNative ? "/app-icon.png" : "/logo.png"} 
            alt="Mystic Tarot" 
            style={{ 
              height: isNative ? '60px' : '90px', 
              width: 'auto',
              borderRadius: isNative ? '12px' : '0'
            }} 
          />
        </div>
        <div className="nav-actions">
          {/* Google Play Button - SADECE WEB'DE GÖSTERİLİR */}
          {!isNative && (
            <a 
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-play-btn"
              title={i18n.language === 'tr' ? 'Uygulamayı İndir' : 'Download App'}
            >
              ▶️ {i18n.language === 'tr' ? 'Uygulamayı İndir' : 'Get App'}
            </a>
          )}
          <button className="lang-toggle" onClick={toggleLanguage}>
            {i18n.language === 'en' ? '🇹🇷 TR' : '🇬🇧 EN'}
          </button>
          <button className="btn-ghost" onClick={() => onShowAuth('login')}>
            {t('hero.login')}
          </button>
          <button className="btn-primary" onClick={() => onShowAuth('register')}>
            {t('hero.register')}
          </button>
        </div>
      </nav>

      <div className="hero-content">
        <motion.h1
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="hero-title"
        >
          {t('hero.title')}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="hero-subtitle"
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* CTA Buttons - Register + Download (Download sadece web'de) */}
        <div className="hero-cta-buttons">
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="btn-hero"
            onClick={() => onShowAuth('register')}
          >
            {t('hero.cta')} ✨
          </motion.button>
          
          {/* Google Play CTA - SADECE WEB'DE */}
          {!isNative && (
            <motion.a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="btn-hero-play"
            >
              <span style={{ fontSize: '1.3rem' }}>▶️</span>
              {i18n.language === 'tr' ? 'Google Play\'den İndir' : 'Get on Google Play'}
            </motion.a>
          )}
        </div>

        <div className="features-grid">
          {/* YENİ SIRALAMA: Kahve → İlişki → Tarot → Burç */}
          {[
            { title: t('features.coffee.title'), desc: t('features.coffee.desc'), icon: '☕', img: 'https://images.unsplash.com/photo-1615829332206-22479388eecc' },
            { title: t('features.relationship.title'), desc: t('features.relationship.desc'), icon: '💞', img: 'https://images.unsplash.com/photo-1518199266791-5375a83190b7' },
            { title: t('features.tarot.title'), desc: t('features.tarot.desc'), icon: '🔮', img: 'https://images.unsplash.com/photo-1671013033034-5ea58e9c5008' },
            { title: t('features.horoscope.title'), desc: t('features.horoscope.desc'), icon: '♈', img: 'https://images.unsplash.com/photo-1578433805614-747d066afebe' }
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + i * 0.2, duration: 0.5 }}
              className="feature-card"
              style={{ backgroundImage: `url(${feature.img})` }}
            >
              <div className="feature-overlay"></div>
              <div className="feature-content">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <footer className="hero-footer">
        <div className="footer-content">
          <p>© 2025 Mystic Tarot. {t('footer.rights')}</p>
          <Link to="/privacy" className="footer-link">{t('footer.privacy')}</Link>
        </div>
      </footer>
    </div>
  );
};

// Auth Modal
const AuthModal = ({ mode, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    birthdate: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    zodiac: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authView, setAuthView] = useState(mode); // 'login', 'register', 'forgot', 'reset'
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Custom dropdown states
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showZodiacPicker, setShowZodiacPicker] = useState(false);

  const months = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
  const zodiacSigns = [
    { value: 'aries', label: '♈ Koç' },
    { value: 'taurus', label: '♉ Boğa' },
    { value: 'gemini', label: '♊ İkizler' },
    { value: 'cancer', label: '♋ Yengeç' },
    { value: 'leo', label: '♌ Aslan' },
    { value: 'virgo', label: '♍ Başak' },
    { value: 'libra', label: '♎ Terazi' },
    { value: 'scorpio', label: '♏ Akrep' },
    { value: 'sagittarius', label: '♐ Yay' },
    { value: 'capricorn', label: '♑ Oğlak' },
    { value: 'aquarius', label: '♒ Kova' },
    { value: 'pisces', label: '♓ Balık' }
  ];

  const updateBirthdate = (day, month, year) => {
    if (year && month && day) {
      const birthdate = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      setFormData(prev => ({ ...prev, birthdate }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      if (authView === 'forgot') {
        await axios.post(`${API_URL}/api/auth/forgot-password`, { email: formData.email });
        setSuccessMessage('Sıfırlama kodu e-posta adresinize gönderildi. Lütfen kodunuzu girin.');
        setAuthView('reset');
      } else if (authView === 'reset') {
        await axios.post(`${API_URL}/api/auth/reset-password`, {
          email: formData.email,
          code: resetCode,
          new_password: newPassword
        });
        setSuccessMessage('Şifreniz başarıyla güncellendi! Giriş yapabilirsiniz.');
        setTimeout(() => {
          setAuthView('login');
          setSuccessMessage('');
          setFormData(prev => ({ ...prev, password: '' }));
        }, 2000);
      } else {
        const endpoint = authView === 'login' ? '/api/auth/login' : '/api/auth/register';
        
        // Add partner attribution for registration
        let requestData = { ...formData };
        if (authView === 'register') {
          const partnerAttribution = getPartnerAttribution();
          if (partnerAttribution) {
            requestData.partner_source = partnerAttribution.partner_slug;
            requestData.partner_id = partnerAttribution.partner_id;
            requestData.campaign_id = partnerAttribution.campaign_id;
            console.log('🎯 Registering with partner attribution:', partnerAttribution);
          }
        }
        
        const { data } = await axios.post(`${API_URL}${endpoint}`, requestData);
        
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Track analytics events
        if (authView === 'login') {
          trackLoginSuccess('email');
        } else {
          trackSignupSuccess('email', requestData.partner_id || null);
        }
        
        onSuccess(data.user);
        onClose();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Bir hata oluştu';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Custom Picker Modal Component
  const PickerModal = ({ show, onClose, title, children }) => {
    if (!show) return null;
    return ReactDOM.createPortal(
      <div 
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
        onClick={onClose}
      >
        <div 
          style={{
            background: '#1a1a2e', borderRadius: '16px', padding: '20px',
            width: '85%', maxWidth: '300px', maxHeight: '60vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
            border: '1px solid rgba(138,43,226,0.5)',
            boxShadow: '0 0 30px rgba(138,43,226,0.3)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ color: '#ffd700', fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', textAlign: 'center' }}>
            {title}
          </div>
          <div style={{ overflowY: 'auto', flex: 1, paddingRight: '5px' }}>
            {children}
          </div>
          <button 
            onClick={onClose}
            style={{
              marginTop: '15px', padding: '12px', background: 'rgba(138,43,226,0.3)',
              border: '1px solid rgba(138,43,226,0.5)', borderRadius: '8px', color: 'white', fontSize: '16px', cursor: 'pointer'
            }}
          >
            Kapat
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const PickerItem = ({ selected, onClick, children }) => (
    <div
      onClick={onClick}
      style={{
        padding: '14px 16px', margin: '4px 0', borderRadius: '8px',
        background: selected ? 'rgba(138,43,226,0.5)' : 'rgba(255,255,255,0.05)',
        color: selected ? '#ffd700' : 'white', cursor: 'pointer',
        fontSize: '16px', textAlign: 'center',
        border: selected ? '1px solid #ffd700' : '1px solid transparent'
      }}
    >
      {children}
    </div>
  );

  const CustomSelect = ({ value, placeholder, onClick }) => (
    <div
      onClick={onClick}
      style={{
        flex: 1, padding: '12px', fontSize: '16px',
        background: 'rgba(255,255,255,0.1)', color: value ? 'white' : 'rgba(255,255,255,0.5)',
        border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
        cursor: 'pointer', textAlign: 'center', minWidth: '60px'
      }}
    >
      {value || placeholder}
    </div>
  );

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      key={`modal-overlay-${mode}`}
    >
      <motion.div
        className="modal-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>✕</button>
        
        <h2>
          {authView === 'login' ? t('auth.login') : 
           authView === 'register' ? t('auth.register') : 
           authView === 'forgot' ? 'Şifremi Unuttum' :
           'Şifre Sıfırlama'}
        </h2>
        
        <form onSubmit={handleSubmit} className="auth-form">
          {authView === 'register' && (
            <>
              <input
                type="text"
                placeholder={t('auth.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="register-name-input"
              />
              
              <div className="form-field-with-label">
                <label className="form-label">
                  {t('auth.birthdate')}
                  <span className="form-label-hint"> (Opsiyonel)</span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <CustomSelect 
                    value={formData.birthDay} 
                    placeholder="Gün" 
                    onClick={() => setShowDayPicker(true)} 
                  />
                  <CustomSelect 
                    value={formData.birthMonth ? months[parseInt(formData.birthMonth) - 1] : ''} 
                    placeholder="Ay" 
                    onClick={() => setShowMonthPicker(true)} 
                  />
                  <CustomSelect 
                    value={formData.birthYear} 
                    placeholder="Yıl" 
                    onClick={() => setShowYearPicker(true)} 
                  />
                </div>
              </div>
            </>
          )}
          
          <input
            type="email"
            placeholder={t('auth.email')}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            data-testid="auth-email-input"
          />
          
          {(authView === 'login' || authView === 'register') && (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.password')}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                style={{ paddingRight: '48px', width: '100%', boxSizing: 'border-box' }}
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                data-testid="toggle-password-visibility"
                style={{
                  position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center',
                  zIndex: 20
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}

          {authView === 'reset' && (
            <>
              <input
                type="text"
                placeholder="6 haneli sıfırlama kodu"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '8px' }}
                data-testid="reset-code-input"
              />
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Yeni şifre"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ paddingRight: '48px', width: '100%', boxSizing: 'border-box' }}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  data-testid="toggle-new-password-visibility"
                  style={{
                    position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center',
                    zIndex: 20
                  }}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </>
          )}

          {authView === 'register' && (
            <div
              onClick={() => setShowZodiacPicker(true)}
              data-testid="zodiac-picker-trigger"
              style={{
                padding: '14px', fontSize: '16px',
                background: 'rgba(255,255,255,0.1)', 
                color: formData.zodiac ? 'white' : 'rgba(255,255,255,0.5)',
                border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                cursor: 'pointer', textAlign: 'left'
              }}
            >
              {formData.zodiac 
                ? zodiacSigns.find(z => z.value === formData.zodiac)?.label 
                : t('auth.zodiac')}
            </div>
          )}

          {successMessage && <div data-testid="auth-success-message" style={{ color: '#4ade80', padding: '10px', background: 'rgba(74,222,128,0.1)', borderRadius: '8px', textAlign: 'center', fontSize: '14px' }}>{successMessage}</div>}
          {error && <div className="error-message" data-testid="auth-error-message">{error}</div>}
          
          <button type="submit" className="btn-primary" disabled={loading} data-testid="auth-submit-button">
            {loading ? t('common.loading') : 
             authView === 'login' ? t('auth.login') : 
             authView === 'register' ? t('auth.register') :
             authView === 'forgot' ? 'Kod Gönder' :
             'Şifreyi Sıfırla'}
          </button>

          {authView === 'login' && (
            <button
              type="button"
              onClick={() => { setAuthView('forgot'); setError(''); setSuccessMessage(''); }}
              data-testid="forgot-password-link"
              style={{
                background: 'none', border: 'none', color: 'rgba(255,215,0,0.8)',
                cursor: 'pointer', fontSize: '14px', textDecoration: 'underline',
                marginTop: '4px', padding: '4px'
              }}
            >
              Şifremi Unuttum
            </button>
          )}

          {(authView === 'forgot' || authView === 'reset') && (
            <button
              type="button"
              onClick={() => { setAuthView('login'); setError(''); setSuccessMessage(''); setResetCode(''); setNewPassword(''); }}
              data-testid="back-to-login-link"
              style={{
                background: 'none', border: 'none', color: 'rgba(255,215,0,0.8)',
                cursor: 'pointer', fontSize: '14px', textDecoration: 'underline',
                marginTop: '4px', padding: '4px'
              }}
            >
              Giriş Yap'a Dön
            </button>
          )}

          {(authView === 'login' || authView === 'register') && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '8px 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>veya</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
              </div>
              <button
                type="button"
                onClick={handleGoogleLogin}
                data-testid="google-login-button"
                style={{
                  width: '100%', padding: '14px 20px', fontSize: '15px', fontWeight: '600',
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px', color: 'white', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.14)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgba(255,255,255,0.08)'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Google ile {authView === 'login' ? 'Giriş Yap' : 'Kayıt Ol'}
              </button>
            </>
          )}
        </form>

        {/* Day Picker */}
        <PickerModal show={showDayPicker} onClose={() => setShowDayPicker(false)} title="Gün Seçin">
          {[...Array(31)].map((_, i) => (
            <PickerItem 
              key={i+1} 
              selected={formData.birthDay === String(i+1)}
              onClick={() => {
                const day = String(i+1);
                setFormData(prev => ({ ...prev, birthDay: day }));
                updateBirthdate(day, formData.birthMonth, formData.birthYear);
                setShowDayPicker(false);
              }}
            >
              {i+1}
            </PickerItem>
          ))}
        </PickerModal>

        {/* Month Picker */}
        <PickerModal show={showMonthPicker} onClose={() => setShowMonthPicker(false)} title="Ay Seçin">
          {months.map((m, i) => (
            <PickerItem 
              key={i+1} 
              selected={formData.birthMonth === String(i+1)}
              onClick={() => {
                const month = String(i+1);
                setFormData(prev => ({ ...prev, birthMonth: month }));
                updateBirthdate(formData.birthDay, month, formData.birthYear);
                setShowMonthPicker(false);
              }}
            >
              {m}
            </PickerItem>
          ))}
        </PickerModal>

        {/* Year Picker */}
        <PickerModal show={showYearPicker} onClose={() => setShowYearPicker(false)} title="Yıl Seçin">
          {[...Array(80)].map((_, i) => {
            const year = new Date().getFullYear() - 10 - i;
            return (
              <PickerItem 
                key={year} 
                selected={formData.birthYear === String(year)}
                onClick={() => {
                  const y = String(year);
                  setFormData(prev => ({ ...prev, birthYear: y }));
                  updateBirthdate(formData.birthDay, formData.birthMonth, y);
                  setShowYearPicker(false);
                }}
              >
                {year}
              </PickerItem>
            );
          })}
        </PickerModal>

        {/* Zodiac Picker */}
        <PickerModal show={showZodiacPicker} onClose={() => setShowZodiacPicker(false)} title="Burç Seçin">
          {zodiacSigns.map((z) => (
            <PickerItem 
              key={z.value} 
              selected={formData.zodiac === z.value}
              onClick={() => {
                setFormData(prev => ({ ...prev, zodiac: z.value }));
                setShowZodiacPicker(false);
              }}
            >
              {z.label}
            </PickerItem>
          ))}
        </PickerModal>
      </motion.div>
    </motion.div>
  );
};

// Dashboard
const Dashboard = ({ user, onLogout, onUserUpdate }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('horoscope'); // Ücretsiz burç ilk sırada!
  const [currentUser, setCurrentUser] = useState(user);
  const [showGiftModal, setShowGiftModal] = useState(false); // Gift Modal State

  // Credit hook
  const { 
    credits, 
    subscriptionType, 
    isUnlimited,
    refreshSubscriptionStatus 
  } = require('./hooks/useSubscription').default(user?.id);

  // Update user when credits change
  useEffect(() => {
    if (credits !== undefined) {
      setCurrentUser(prev => ({ ...prev, credits, subscription_type: subscriptionType }));
    }
  }, [credits, subscriptionType]);

  // Refresh credits on dashboard mount to prevent stale display
  useEffect(() => {
    if (refreshSubscriptionStatus) {
      refreshSubscriptionStatus();
    }
  }, [refreshSubscriptionStatus]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'tr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    console.log('Language changed to:', newLang);
    window.location.reload();
  };

  // Refresh credits after any action
  const handleRefreshCredits = async () => {
    await refreshSubscriptionStatus();
  };

  const displayCredits = isUnlimited ? '∞' : (credits ?? currentUser.credits ?? 5);
  const tierLabel = subscriptionType === 'free' ? '' : ` (${subscriptionType?.toUpperCase()})`;

  // PHASE 3 TAB CONFIG - Yeni sıralama ve maliyetler
  // Admin email listesi - sadece admin panel erişimi için
  const ADMIN_EMAILS = [
    'admin@mysticai.com',
    'hak.dalmis@gmail.com', 
    'hakandalmis@paracini.com'
  ];
  const isAdmin = ADMIN_EMAILS.includes(currentUser?.email);
  
  // Oracle erişim kontrolü - Plan bazlı
  // Free: Kilitli görünür (upgrade CTA gösterilir)
  // Premium/Pro/Elite: Tam erişim
  // Admin: Sınırsız erişim
  const isPremiumUser = ['starter', 'premium', 'pro', 'elite'].includes(subscriptionType);
  const canAccessOracle = isAdmin || isPremiumUser;
  
  const tabConfig = [
    { id: 'horoscope', icon: '♈', label: 'Burç', cost: 0, free: true },
    { id: 'compatibility', icon: '💕', label: 'Aşk Uyumu', cost: 6 },
    { id: 'natal', icon: '🗺️', label: 'Doğum Haritası', cost: 8 },
    { id: 'tarot', icon: '🔮', label: 'Tarot', cost: 5 },
    { id: 'hybrid', icon: '✨', label: 'Hibrit Analiz', cost: 10 },
    { id: 'coach', icon: '🧠', label: 'AI Koç', cost: 2 },
    { id: 'coffee', icon: '☕', label: 'Kahve Falı', cost: 3 },
    { id: 'relationship', icon: '❤️', label: 'İlişki Analizi', cost: 6 },
    // Gift Feature - Fal Hediye Et + Hediyelerim
    { id: 'gift', icon: '🎁', label: 'Hediye Et', cost: 0, isAction: true, action: () => setShowGiftModal(true) },
    { id: 'giftHistory', icon: '📦', label: 'Hediyelerim', cost: 0, free: true },
    // Oracle - Tüm kullanıcılara görünür, Free kullanıcılar için kilitli
    { id: 'oracle', icon: '🔮', label: 'Oracle', cost: 5, isLink: true, href: '/oracle', premium: true, locked: !canAccessOracle },
    // Palm Oracle - AI El Falı (v2.5.0)
    { id: 'palm', icon: '✋', label: 'El Falı', cost: 5, isLink: true, href: '/palm', premium: true, locked: !canAccessOracle },
  ];

  return (
    <div className="dashboard-container">
      <nav className="dashboard-nav">
        <div className="logo">
          <img src="/app-icon.png" alt="Mystic Tarot" style={{ height: '60px', width: 'auto', borderRadius: '12px' }} />
        </div>
        <div className="nav-actions">
          {/* Credit Display */}
          <div className="credit-badge" onClick={() => setActiveTab('premium')} style={{ cursor: 'pointer' }}>
            <span className="credit-icon">💎</span>
            <span className="credit-count">{displayCredits}</span>
            <span className="credit-label">{t('credits.remaining')}{tierLabel}</span>
          </div>
          
          <span className="user-name">{currentUser.name}</span>
          <button className="lang-toggle" onClick={toggleLanguage}>
            {i18n.language === 'en' ? '🇹🇷 TR' : '🇬🇧 EN'}
          </button>
          {subscriptionType === 'free' && (
            <button className="btn-premium" onClick={() => setActiveTab('premium')}>
              👑 {t('nav.premium')}
            </button>
          )}
          {isAdmin && (
            <button 
              className="btn-primary" 
              onClick={() => window.location.href = '/admin'}
              style={{ marginRight: '10px' }}
            >
              🔐 Admin Panel
            </button>
          )}
          <button className="btn-ghost" onClick={onLogout}>
            {t('nav.logout')}
          </button>
        </div>
      </nav>

      <div className="dashboard-tabs" style={{ overflowX: 'auto', whiteSpace: 'nowrap', padding: '0 10px' }}>
        {tabConfig.map((tab) => (
          tab.isLink ? (
            tab.locked ? (
              // Locked premium feature - show upgrade CTA
              <button
                key={tab.id}
                className={`tab-btn`}
                onClick={() => setActiveTab('premium')}
                data-testid={`tab-${tab.id}`}
                style={{ 
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, rgba(100,100,100,0.3), rgba(80,80,80,0.2))',
                  border: '1px solid rgba(100,100,100,0.5)',
                  opacity: 0.85
                }}
              >
                <span style={{ marginRight: '4px' }}>{tab.icon}</span>
                {tab.label}
                <span className="credit-cost-badge" style={{ 
                  background: 'rgba(251,191,36,0.2)',
                  color: '#fbbf24'
                }}>
                  🔒 PREMIUM
                </span>
              </button>
            ) : (
              <Link
                key={tab.id}
                to={tab.href}
                className={`tab-btn`}
                data-testid={`tab-${tab.id}`}
                style={{ 
                  flexShrink: 0, 
                  textDecoration: 'none',
                  background: tab.premium ? 'linear-gradient(135deg, rgba(124,58,237,0.3), rgba(168,85,247,0.2))' : undefined,
                  border: tab.premium ? '1px solid rgba(168,85,247,0.5)' : undefined
                }}
              >
                <span style={{ marginRight: '4px' }}>{tab.icon}</span>
                {tab.label}
                <span className="credit-cost-badge" style={{ 
                  background: tab.premium ? 'rgba(251,191,36,0.2)' : 'rgba(139,92,246,0.2)',
                  color: tab.premium ? '#fbbf24' : '#c084fc'
                }}>
                  {tab.premium ? '✨ PREMIUM' : `${tab.cost} 💎`}
                </span>
              </Link>
            )
          ) : tab.isAction ? (
            <button
              key={tab.id}
              className={`tab-btn`}
              onClick={tab.action}
              data-testid={`tab-${tab.id}`}
              style={{ 
                flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(139,92,246,0.2))',
                border: '1px solid rgba(236,72,153,0.4)'
              }}
            >
              <span style={{ marginRight: '4px' }}>{tab.icon}</span>
              {tab.label}
              <span className="credit-cost-badge" style={{ 
                background: 'rgba(236,72,153,0.3)',
                color: '#f472b6'
              }}>
                HEDİYE
              </span>
            </button>
          ) : (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{ flexShrink: 0 }}
            >
              <span style={{ marginRight: '4px' }}>{tab.icon}</span>
              {tab.label}
              <span className="credit-cost-badge" style={{ 
                background: tab.free ? 'rgba(74,222,128,0.2)' : 'rgba(139,92,246,0.2)',
                color: tab.free ? '#4ade80' : '#c084fc'
              }}>
                {tab.free ? 'ÜCRETSİZ' : `${tab.cost} 💎`}
              </span>
            </button>
          )
        ))}
      </div>

      <div className="dashboard-content">
        <AnimatePresence mode="wait">
          {activeTab === 'horoscope' && <HoroscopeTab key={`horoscope-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'compatibility' && <CompatibilityTab key={`compatibility-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'natal' && <NatalChartTab key={`natal-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'tarot' && <TarotTab key={`tarot-${currentUser.id}`} user={currentUser} onUpgradeClick={() => setActiveTab('premium')} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'hybrid' && <HybridAnalysisTab key={`hybrid-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'coach' && <AICoachTab key={`coach-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'coffee' && <CoffeeTab key={`coffee-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'relationship' && <RelationshipTab key={`relationship-${currentUser.id}`} user={currentUser} onCreditUsed={handleRefreshCredits} />}
          {activeTab === 'giftHistory' && <GiftHistoryTab key={`giftHistory-${currentUser.id}`} />}
          {activeTab === 'premium' && <PremiumTab key={`premium-${currentUser.id}`} user={currentUser} />}
        </AnimatePresence>
      </div>

      {/* Gift Fortune Modal */}
      <GiftFortuneModal 
        isOpen={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        userCredits={displayCredits === '∞' ? 999 : displayCredits}
        onGiftSent={(cost) => {
          handleRefreshCredits();
        }}
      />
    </div>
  );
};

// Tarot Tab
const TarotTab = ({ user, onUpgradeClick, onCreditUsed }) => {
  const { t, i18n } = useTranslation();
  const [spreadType, setSpreadType] = useState('three_card');
  const [question, setQuestion] = useState('');
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(false);

  const creditCost = 5; // Tarot - 5 kredi
  // Elite/Pro users have unlimited credits
  const isUnlimited = user.subscription_type === 'elite' || user.subscription_type === 'pro' || user.credits === -1;
  const hasEnoughCredits = isUnlimited || user.subscription_type === 'starter' || (user.credits || 0) >= creditCost;

  const handleDrawCards = async () => {
    // Check credits
    if (!hasEnoughCredits) {
      if (window.confirm(t('credits.insufficient') + '. ' + (i18n.language === 'tr' ? 'Şimdi yükseltmek ister misiniz?' : 'Would you like to upgrade now?'))) {
        onUpgradeClick();
      }
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/api/tarot/reading`,
        { spread_type: spreadType, question, language: i18n.language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReading(data);
      // Refresh credits after use
      if (onCreditUsed) onCreditUsed();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating reading');
    } finally {
      setLoading(false);
    }
  };

  if (reading) {
    return (
      <motion.div
        key="reading-result"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="reading-result"
      >
        <h2>{t('tarot.interpretation')}</h2>
        
        <div className="cards-display">
          {reading.cards.map((card, i) => {
            // Suit icon mapping
            const suitIcons = {
              major: '✨',
              wands: '🪄',
              cups: '🏆',
              swords: '⚔️',
              pentacles: '💰'
            };
            
            // Gradient colors by suit
            const suitGradients = {
              major: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              wands: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              cups: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              swords: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              pentacles: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
            };
            
            return (
              <motion.div
                key={i}
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ delay: i * 0.2 }}
                className="tarot-card tarot-card-modern"
                data-suit={card.suit}
                style={{
                  background: suitGradients[card.suit] || suitGradients.major
                }}
              >
                <div className="card-suit-icon-large">
                  {suitIcons[card.suit] || suitIcons.major}
                </div>
                <div className="card-name-modern">
                  {i18n.language === 'tr' ? card.name_tr : card.name}
                </div>
                <div className="card-id-badge">{card.id}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="interpretation-text">
          {reading.interpretation}
        </div>

        <button className="btn-primary" onClick={() => setReading(null)}>
          {t('tarot.newReading')}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="tarot-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="tarot-form"
    >
      <h2>🔮 {t('nav.tarot')}</h2>
      
      <div className="form-group">
        <label>{t('tarot.selectSpread')}</label>
        <div className="spread-options">
          {['three_card', 'five_card', 'ten_card'].map((type) => (
            <button
              key={type}
              className={`spread-btn ${spreadType === type ? 'active' : ''}`}
              onClick={() => setSpreadType(type)}
            >
              {type === 'three_card' && t('tarot.threeCard')}
              {type === 'five_card' && t('tarot.fiveCard')}
              {type === 'ten_card' && t('tarot.tenCard')}
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>{t('tarot.question')}</label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('tarot.question')}
          rows={3}
        />
      </div>

      <button
        className="btn-primary btn-large"
        onClick={handleDrawCards}
        disabled={loading}
      >
        {loading ? t('common.loading') : (user.premium ? t('tarot.drawCards') : `👑 ${t('tarot.drawCards') || 'Kart Çek'} (Premium)`)}
      </button>

      {!hasEnoughCredits && (
        <div className="limit-notice" style={{ background: 'rgba(139, 92, 246, 0.15)', padding: '1rem', borderRadius: '10px', marginTop: '1rem' }}>
          ⭐ {i18n.language === 'tr' 
            ? `Bu işlem ${creditCost} kredi gerektirir. Krediniz: ${user.credits || 0}` 
            : `This action requires ${creditCost} credits. Your credits: ${user.credits || 0}`}
        </div>
      )}
    </motion.div>
  );
};

// Horoscope Tab - ÜCRETSİZ! 🎉
const HoroscopeTab = ({ user, onCreditUsed }) => {
  const { t, i18n } = useTranslation();
  const [zodiac, setZodiac] = useState(user.zodiac || 'aries');
  const [period, setPeriod] = useState('daily');
  const [horoscope, setHoroscope] = useState(null);
  const [loading, setLoading] = useState(false);

  // GÜNLÜK BURÇ ARTIK ÜCRETSİZ!
  const creditCost = 0;

  const handleReadHoroscope = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/api/horoscope/reading`,
        { zodiac, period, language: i18n.language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHoroscope(data);
      // No credit deduction needed for free feature
    } catch (err) {
      alert(err.response?.data?.detail || 'Error fetching horoscope');
    } finally {
      setLoading(false);
    }
  };

  if (horoscope) {
    return (
      <motion.div
        key="horoscope-result"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="horoscope-result"
      >
        <h2>♈ {t('nav.horoscope')}</h2>
        
        {/* FREE BADGE */}
        <div style={{ 
          display: 'inline-block', 
          background: 'rgba(74,222,128,0.2)', 
          color: '#4ade80', 
          padding: '4px 12px', 
          borderRadius: '20px', 
          fontSize: '12px', 
          fontWeight: '600',
          marginBottom: '16px'
        }}>
          ✓ ÜCRETSİZ
        </div>
        
        <div className="lucky-stats">
          <div className="lucky-item">
            <span className="lucky-label">{t('horoscope.luckyNumber')}</span>
            <span className="lucky-value">{horoscope.lucky_number}</span>
          </div>
          <div className="lucky-item">
            <span className="lucky-label">{t('horoscope.luckyColor')}</span>
            <span className="lucky-value">{horoscope.lucky_color}</span>
          </div>
          <div className="lucky-item">
            <span className="lucky-label">{t('horoscope.luckyStone')}</span>
            <span className="lucky-value">{horoscope.lucky_stone}</span>
          </div>
        </div>

        <div className="horoscope-content">
          {horoscope.content}
        </div>

        <button className="btn-primary" onClick={() => setHoroscope(null)}>
          {t('common.back')}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="horoscope-form"
    >
      <h2>♈ {t('nav.horoscope')}</h2>
      
      {/* FREE BANNER */}
      <div style={{ 
        background: 'linear-gradient(90deg, rgba(74,222,128,0.1), rgba(74,222,128,0.05))', 
        border: '1px solid rgba(74,222,128,0.3)',
        borderRadius: '12px',
        padding: '12px 16px',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        <span style={{ color: '#4ade80', fontWeight: '600' }}>🎉 Günlük Burç Yorumu ÜCRETSİZ!</span>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: '4px 0 0' }}>
          Sınırsız günlük burç yorumu alabilirsiniz
        </p>
      </div>
      
      <div className="form-group">
        <label>{t('horoscope.selectSign')}</label>
        <select value={zodiac} onChange={(e) => setZodiac(e.target.value)} className="zodiac-select" data-testid="horoscope-zodiac-select">
          <option value="aries">♈ Aries / Koç</option>
          <option value="taurus">♉ Taurus / Boğa</option>
          <option value="gemini">♊ Gemini / İkizler</option>
          <option value="cancer">♋ Cancer / Yengeç</option>
          <option value="leo">♌ Leo / Aslan</option>
          <option value="virgo">♍ Virgo / Başak</option>
          <option value="libra">♎ Libra / Terazi</option>
          <option value="scorpio">♏ Scorpio / Akrep</option>
          <option value="sagittarius">♐ Sagittarius / Yay</option>
          <option value="capricorn">♑ Capricorn / Oğlak</option>
          <option value="aquarius">♒ Aquarius / Kova</option>
          <option value="pisces">♓ Pisces / Balık</option>
        </select>
      </div>

      <div className="form-group">
        <label>Period</label>
        <div className="period-options">
          {['daily', 'weekly', 'monthly'].map((p) => (
            <button
              key={p}
              className={`period-btn ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
              data-testid={`horoscope-period-${p}`}
            >
              {t(`horoscope.${p}`)}
            </button>
          ))}
        </div>
      </div>

      <button
        className="btn-primary btn-large"
        onClick={handleReadHoroscope}
        disabled={loading}
        data-testid="horoscope-read-button"
      >
        {loading ? t('common.loading') : `${t('horoscope.readNow')} ✨`}
      </button>
    </motion.div>
  );
};

// Coffee Tab
const CoffeeTab = ({ user, onCreditUsed }) => {
  const { t, i18n } = useTranslation();
  const [files, setFiles] = useState([null, null, null]);
  const [previews, setPreviews] = useState([null, null, null]);
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(false);

  const creditCost = 3; // Kahve Falı - 3 kredi
  const isBlocked = false; // Artık herkese açık
  // Elite/Pro users have unlimited credits
  const isUnlimited = user.subscription_type === 'elite' || user.subscription_type === 'pro' || user.credits === -1;
  const hasEnoughCredits = isUnlimited || (user.credits || 0) >= creditCost;

  const handleFileChange = (index) => (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const newFiles = [...files];
      const newPreviews = [...previews];
      newFiles[index] = selectedFile;
      newPreviews[index] = URL.createObjectURL(selectedFile);
      setFiles(newFiles);
      setPreviews(newPreviews);
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...files];
    const newPreviews = [...previews];
    if (newPreviews[index]) {
      URL.revokeObjectURL(newPreviews[index]);
    }
    newFiles[index] = null;
    newPreviews[index] = null;
    setFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleAnalyze = async () => {
    // Check if blocked for free users
    if (isBlocked) {
      alert(i18n.language === 'tr' 
        ? 'Kahve falı sadece abonelere açıktır. Lütfen bir plan seçin.'
        : 'Coffee fortune is only available for subscribers. Please choose a plan.');
      return;
    }
    
    // Check credits
    if (!hasEnoughCredits) {
      alert(i18n.language === 'tr' 
        ? `Yetersiz kredi. Bu işlem ${creditCost} kredi gerektirir.`
        : `Insufficient credits. This action requires ${creditCost} credits.`);
      return;
    }
    
    const uploadedFiles = files.filter(f => f !== null);
    if (uploadedFiles.length === 0) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      uploadedFiles.forEach((file, index) => {
        formData.append('files', file);
      });
      formData.append('language', i18n.language);

      const { data } = await axios.post(
        `${API_URL}/api/coffee/reading`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setReading(data);
      if (onCreditUsed) onCreditUsed();
    } catch (err) {
      // Handle validation error specifically
      const errorData = err.response?.data?.detail;
      
      if (errorData?.validation_failed) {
        // Show friendly validation error
        alert(`☕ ${errorData.message}`);
      } else if (typeof errorData === 'string') {
        alert(errorData);
      } else {
        alert(i18n.language === 'tr' ? 'Fal yorumu yapılırken bir hata oluştu' : 'Error analyzing fortune');
      }
    } finally {
      setLoading(false);
    }
  };

  if (reading) {
    return (
      <motion.div
        key="coffee-result"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="coffee-result"
      >
        <h2>☕ {t('coffee.reading')}</h2>
        
        <div className="coffee-images">
          {previews.filter(p => p !== null).map((preview, idx) => (
            <div key={idx} className="coffee-image">
              <img src={preview} alt={`Coffee cup ${idx + 1}`} />
            </div>
          ))}
        </div>

        <div className="fortune-text">
          {reading.interpretation}
        </div>

        <button className="btn-primary" onClick={() => {
          setReading(null);
          setFiles([null, null, null]);
          setPreviews([null, null, null]);
        }}>
          {t('common.back')}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="coffee-form"
    >
      <h2>☕ {t('nav.coffee')}</h2>
      
      <p style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--text-dim)' }}>
        {i18n.language === 'tr' ? '📸 3 fotoğraf yükleyin (fincanın içi, sağ, sol)' : '📸 Upload 3 photos (inside, right, left)'}
      </p>
      
      {/* Important notice about real coffee cups */}
      <div style={{
        background: 'rgba(245, 158, 11, 0.15)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '10px',
        padding: '12px 16px',
        marginBottom: '1.5rem',
        textAlign: 'center',
        fontSize: '0.9rem'
      }}>
        ⚠️ {i18n.language === 'tr' 
          ? 'Sadece gerçek Türk kahvesi fincanı fotoğrafları kabul edilir. Başka görseller için yorum yapılmaz.'
          : 'Only real Turkish coffee cup photos are accepted. Other images will not be interpreted.'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[0, 1, 2].map((index) => (
          <div key={index} className="upload-zone" style={{ minHeight: '200px' }}>
            {previews[index] ? (
              <div className="preview-container">
                <img src={previews[index]} alt={`Preview ${index + 1}`} className="preview-image" />
                <button className="btn-change" onClick={() => handleRemoveFile(index)}>
                  {i18n.language === 'tr' ? 'Değiştir' : 'Change'}
                </button>
              </div>
            ) : (
              <div className="upload-label">
                <div className="upload-icon">📷</div>
                <p>{i18n.language === 'tr' ? `Fotoğraf ${index + 1}` : `Photo ${index + 1}`}</p>
                
                {/* Galeri seçimi */}
                <label htmlFor={`file-gallery-${index}`} style={{ display: 'block', marginBottom: '0.5rem' }}>
                  <button 
                    type="button"
                    className="btn-secondary"
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem',
                      marginBottom: '0.5rem'
                    }}
                    onClick={() => document.getElementById(`file-gallery-${index}`).click()}
                  >
                    📂 {i18n.language === 'tr' ? 'Galeriden Seç' : 'From Gallery'}
                  </button>
                  <input
                    id={`file-gallery-${index}`}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange(index)}
                    style={{ display: 'none' }}
                  />
                </label>

                {/* Kamera ile çekimi */}
                <label htmlFor={`file-camera-${index}`}>
                  <button 
                    type="button"
                    className="btn-secondary"
                    style={{ 
                      width: '100%', 
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem'
                    }}
                    onClick={() => document.getElementById(`file-camera-${index}`).click()}
                  >
                    📸 {i18n.language === 'tr' ? 'Kameradan Çek' : 'Take Photo'}
                  </button>
                  <input
                    id={`file-camera-${index}`}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange(index)}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        className="btn-primary btn-large"
        onClick={handleAnalyze}
        disabled={files.filter(f => f !== null).length === 0 || loading || isBlocked || !hasEnoughCredits}
      >
        {loading ? t('common.loading') : `${t('coffee.analyze')} (${creditCost} 💎)`}
      </button>

      {isBlocked && (
        <div className="limit-notice" style={{ background: 'rgba(239, 68, 68, 0.15)', marginTop: '1rem' }}>
          🔒 {i18n.language === 'tr' 
            ? 'Kahve falı sadece abonelere açıktır. Bir plan seçerek sınırsız erişim kazanın!'
            : 'Coffee fortune is only available for subscribers. Choose a plan for unlimited access!'}
        </div>
      )}
      
      {!isBlocked && !hasEnoughCredits && (
        <div className="limit-notice" style={{ marginTop: '1rem' }}>
          ⚠️ {i18n.language === 'tr' 
            ? `Yetersiz kredi. Bu işlem ${creditCost} kredi gerektirir, ${user.credits || 0} krediniz var.`
            : `Insufficient credits. This action requires ${creditCost} credits, you have ${user.credits || 0}.`}
        </div>
      )}
    </motion.div>
  );
};

// Relationship Tab
const RelationshipTab = ({ user, onCreditUsed }) => {
  const { t, i18n } = useTranslation();
  const [analysisType, setAnalysisType] = useState('future_scenario');
  const [relationshipDesc, setRelationshipDesc] = useState('');
  const [partnerDesc, setPartnerDesc] = useState('');
  const [messages, setMessages] = useState('');
  const [situation, setSituation] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const creditCost = 6; // İlişki Analizi - 6 kredi
  // Elite/Pro users have unlimited credits
  const isUnlimited = user.subscription_type === 'elite' || user.subscription_type === 'pro' || user.credits === -1;
  const hasEnoughCredits = isUnlimited || (user.credits || 0) >= creditCost;

  const analysisTypes = [
    { value: 'future_scenario', icon: '🔮', label: t('relationship.futureScenario') },
    { value: 'infidelity_risk', icon: '⚠️', label: t('relationship.infidelityRisk') },
    { value: 'relationship_map', icon: '🗺️', label: t('relationship.relationshipMap') },
    { value: 'red_flags', icon: '🚩', label: t('relationship.redFlags') },
    { value: 'message_analysis', icon: '💬', label: t('relationship.messageAnalysis') },
    { value: 'solution_plan', icon: '📋', label: t('relationship.solutionPlan') },
    { value: 'energy_score', icon: '⚡', label: t('relationship.energyScore') },
    { value: 'partner_mirror', icon: '🪞', label: t('relationship.partnerMirror') }
  ];

  const handleAnalyze = async () => {
    if (!relationshipDesc.trim()) {
      alert(i18n.language === 'tr' ? 'Lütfen ilişkinizi tanımlayın' : 'Please describe your relationship');
      return;
    }

    // Check credits
    if (!hasEnoughCredits) {
      alert(i18n.language === 'tr' 
        ? `Yetersiz kredi. Bu işlem ${creditCost} kredi gerektirir.`
        : `Insufficient credits. This action requires ${creditCost} credits.`);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/api/relationship/analysis`,
        {
          analysis_type: analysisType,
          relationship_description: relationshipDesc,
          partner_description: partnerDesc,
          messages: messages,
          specific_situation: situation,
          language: i18n.language
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      if (onCreditUsed) onCreditUsed();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error performing analysis');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <motion.div
        key="relationship-result"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="relationship-result"
      >
        <h2>❤️ {t('relationship.result')}</h2>
        
        <div className="analysis-type-badge">
          {analysisTypes.find(t => t.value === result.analysis_type)?.icon} {' '}
          {analysisTypes.find(t => t.value === result.analysis_type)?.label}
        </div>

        <div className="analysis-content">
          {result.result.result || result.result}
        </div>

        <button className="btn-primary" onClick={() => setResult(null)}>
          {t('common.back')}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="relationship-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="relationship-form"
    >
      <h2>❤️ {t('relationship.title')}</h2>
      
      <p style={{ 
        textAlign: 'center', 
        fontSize: '1.1rem', 
        color: 'var(--text-dim)', 
        marginBottom: '2rem',
        fontStyle: 'italic'
      }}>
        {i18n.language === 'tr' 
          ? '✨ İlişkinizin görünmeyen tarafını derin analizle keşfedin' 
          : '✨ Discover the hidden side of your relationship with deep analysis'}
      </p>
      
      <div className="form-group">
        <label>{t('relationship.selectType')}</label>
        <div className="analysis-type-grid">
          {analysisTypes.map((type) => (
            <button
              key={type.value}
              className={`analysis-type-btn ${analysisType === type.value ? 'active' : ''}`}
              onClick={() => setAnalysisType(type.value)}
            >
              <span className="type-icon">{type.icon}</span>
              <span className="type-label">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>{t('relationship.describe')}</label>
        <textarea
          value={relationshipDesc}
          onChange={(e) => setRelationshipDesc(e.target.value)}
          placeholder={t('relationship.describe')}
          rows={4}
          required
        />
      </div>

      {['infidelity_risk', 'relationship_map', 'red_flags', 'partner_mirror'].includes(analysisType) && (
        <div className="form-group">
          <label>{t('relationship.partnerDesc')}</label>
          <textarea
            value={partnerDesc}
            onChange={(e) => setPartnerDesc(e.target.value)}
            placeholder={t('relationship.partnerDesc')}
            rows={3}
          />
        </div>
      )}

      {analysisType === 'message_analysis' && (
        <div className="form-group">
          <label>{t('relationship.messages')}</label>
          <textarea
            value={messages}
            onChange={(e) => setMessages(e.target.value)}
            placeholder={t('relationship.messages')}
            rows={4}
          />
        </div>
      )}

      {['solution_plan', 'partner_mirror'].includes(analysisType) && (
        <div className="form-group">
          <label>{t('relationship.situation')}</label>
          <textarea
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            placeholder={t('relationship.situation')}
            rows={3}
          />
        </div>
      )}

      <button
        className="btn-primary btn-large"
        onClick={handleAnalyze}
        disabled={loading || !relationshipDesc.trim() || !hasEnoughCredits}
      >
        {loading ? t('common.loading') : `${t('relationship.analyze')} (${creditCost} 💎)`}
      </button>

      {!hasEnoughCredits && (
        <div className="limit-notice" style={{ marginTop: '1rem' }}>
          ⚠️ {i18n.language === 'tr' 
            ? `Yetersiz kredi. Bu işlem ${creditCost} kredi gerektirir.`
            : `Insufficient credits. This action requires ${creditCost} credits.`}
        </div>
      )}

      <div className="info-notice">
        💡 {i18n.language === 'tr' 
          ? 'Tüm analizler size özel hazırlanır ve sadece rehberlik amaçlıdır.'
          : 'All analyses are personalized and for guidance only.'}
      </div>
    </motion.div>
  );
};

// Premium Tab - YENİ TEK PLAN SİSTEMİ (Mystic Başlangıç Paketi)
const PremiumTab = ({ user }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showWebModal, setShowWebModal] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  // Import subscription hook and platform detection
  const useSubscription = require('./hooks/useSubscription').default;
  const { isNativePlatform } = require('./services/subscriptionService');
  const subscriptionService = require('./services/subscriptionService').default;
  const {
    isPremium,
    isLoading: subscriptionLoading,
    purchaseSubscription,
    restorePurchases,
    credits,
    subscriptionType
  } = useSubscription(user?.id);

  // Check if running on web
  const isWeb = !isNativePlatform();

  // DEBUG: Run billing diagnostics
  const runBillingDebug = async () => {
    setLoading(true);
    try {
      console.log('🔍 Running billing debug...');
      
      // Initialize if needed
      await subscriptionService.initialize(user?.id);
      
      // Get debug info
      const info = await subscriptionService.getBillingDebugInfo();
      setDebugInfo(info);
      setShowDebug(true);
      
      // Also show as alert for easy viewing
      const summary = `
=== BILLING DEBUG ===
Platform: ${info.platform}
Is Native: ${info.isNative}
Initialized: ${info.isInitialized}
SKU: ${info.configuredSKU}
Type: ${info.productType}

Query Results:
- Products found: ${info.queryResults?.productCount || 0}
- Queried SKU: ${info.queryResults?.queriedSKU || 'N/A'}
${info.errors.length > 0 ? '\nErrors: ' + info.errors.join(', ') : ''}
      `.trim();
      
      alert(summary);
      console.log('📋 Debug summary:', summary);
      
    } catch (err) {
      console.error('Debug error:', err);
      alert('Debug error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Tek plan: Mystic Başlangıç Paketi
  const starterPlan = {
    id: 'starter',
    name: 'Mystic Başlangıç Paketi',
    name_en: 'Mystic Starter Pack',
    sku: 'mystic_tarot_single_reading', // Updated SKU
    price: 149,
    credits: 20,
    validity: 30, // gün
    features_tr: [
      '20 kredi',
      '30 gün geçerli',
      'Tüm özellikler',
      'Kişiye özel yorumlar',
      'Reklamsız deneyim'
    ],
    features_en: [
      '20 credits',
      'Valid for 30 days',
      'All features',
      'Personal readings',
      'Ad-free experience'
    ],
    color: '#8b5cf6'
  };

  // Kredi maliyetleri - YENİ FİYATLANDIRMA
  const creditCosts = [
    { icon: '☕', name: i18n.language === 'tr' ? 'Kahve Falı' : 'Coffee Fortune', cost: 3 },
    { icon: '❤️', name: i18n.language === 'tr' ? 'İlişki Analizi' : 'Relationship', cost: 6 },
    { icon: '🔮', name: 'Tarot', cost: 5 },
    { icon: '♈', name: i18n.language === 'tr' ? 'Günlük Burç' : 'Horoscope', cost: 1 }
  ];

  const handlePurchase = async () => {
    // If on web, show modal instead of purchasing
    if (isWeb) {
      setShowWebModal(true);
      return;
    }

    // Native platform - proceed with actual purchase
    setLoading(true);
    try {
      console.log('[Premium] Starting purchase for:', starterPlan.sku);
      await purchaseSubscription(starterPlan.sku);
      
      alert(i18n.language === 'tr' 
        ? `✅ ${starterPlan.name} aktifleştirildi! Sayfa yenileniyor...` 
        : `✅ ${starterPlan.name_en} activated! Reloading page...`);
      
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error('[Premium] Purchase error:', err);
      
      // Handle web purchase block
      if (err.message === 'WEB_PURCHASE_BLOCKED') {
        setShowWebModal(true);
        return;
      }
      
      alert(`❌ ${err.message || 'Purchase failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    setLoading(true);
    try {
      await restorePurchases();
      alert(i18n.language === 'tr' ? '✅ Satın alımlar geri yüklendi!' : '✅ Purchases restored!');
      window.location.reload();
    } catch (err) {
      alert(i18n.language === 'tr' ? '❌ Geri yükleme başarısız.' : '❌ Restore failed.');
    } finally {
      setLoading(false);
    }
  };

  // Web Purchase Block Modal
  const WebPurchaseModal = () => {
    if (!showWebModal) return null;
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="modal-content"
          style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '450px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📱</div>
          
          <h2 style={{ 
            fontSize: '1.5rem', 
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #c084fc, #818cf8)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {i18n.language === 'tr' ? 'Mobil Uygulama Gerekli' : 'Mobile App Required'}
          </h2>
          
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            {i18n.language === 'tr' 
              ? 'Abonelik satın alma işlemleri güvenlik nedeniyle sadece mobil uygulama üzerinden yapılabilir. Lütfen Google Play Store\'dan Mystic Tarot uygulamasını indirin.'
              : 'Subscription purchases can only be made through the mobile app for security reasons. Please download the Mystic Tarot app from Google Play Store.'}
          </p>

          <div style={{
            background: 'rgba(139, 92, 246, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ fontWeight: 'bold', color: starterPlan.color, fontSize: '1.2rem' }}>
              {i18n.language === 'tr' ? starterPlan.name : starterPlan.name_en} - {starterPlan.price} ₺
            </div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
              💎 {starterPlan.credits} {i18n.language === 'tr' ? 'kredi • 30 gün' : 'credits • 30 days'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="https://play.google.com/store/apps/details?id=com.mysticai.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#22c55e',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: 'bold',
                transition: 'transform 0.2s'
              }}
            >
              <span>▶️</span> Google Play
            </a>
            
            <button
              onClick={() => setShowWebModal(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {i18n.language === 'tr' ? 'Kapat' : 'Close'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  // Loading state
  if (subscriptionLoading) {
    return (
      <div className="premium-content" style={{ textAlign: 'center', padding: '60px 20px', minHeight: '400px' }}>
        <div className="premium-crown">⏳</div>
        <h2>{i18n.language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</h2>
      </div>
    );
  }

  // Active subscription view
  if (subscriptionType && subscriptionType !== 'free') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="premium-content"
        style={{ minHeight: '400px' }}
      >
        <div className="premium-crown">👑</div>
        <h2>{i18n.language === 'tr' ? 'Premium Üyesiniz!' : 'You\'re Premium!'}</h2>
        
        <div className="current-plan-info" style={{ 
          background: `linear-gradient(135deg, ${starterPlan.color}20, ${starterPlan.color}10)`,
          border: `2px solid ${starterPlan.color}`,
          borderRadius: '16px',
          padding: '24px',
          margin: '24px 0',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '8px' }}>💎</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: starterPlan.color }}>{credits || 0}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)' }}>
            {i18n.language === 'tr' ? 'Kalan Kredi' : 'Credits Remaining'}
          </div>
        </div>

        <button className="btn-ghost" onClick={handleRestore} disabled={loading} style={{ marginTop: '20px' }}>
          {loading ? '...' : (i18n.language === 'tr' ? '🔄 Senkronize Et' : '🔄 Sync')}
        </button>
      </motion.div>
    );
  }

  // Plan selection view (for free users or users who want more credits)
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="premium-content"
      style={{ minHeight: '400px' }}
    >
      <WebPurchaseModal />
      
      <div className="premium-crown">✨</div>
      <h2>{i18n.language === 'tr' ? 'Kredi Satın Al' : 'Get Credits'}</h2>
      <p className="premium-subtitle" style={{ marginBottom: '24px', color: 'rgba(255,255,255,0.7)' }}>
        {i18n.language === 'tr' 
          ? 'Tüm mistik özelliklere erişim için kredi alın'
          : 'Get credits to unlock all mystical features'}
      </p>

      {/* Mevcut kredi durumu */}
      <div style={{
        background: 'rgba(139, 92, 246, 0.15)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '24px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>
          {i18n.language === 'tr' ? 'Mevcut Krediniz' : 'Current Credits'}
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>
          💎 {user.credits || 0}
        </div>
      </div>

      {/* Rewarded Ads - Ücretsiz Kredi Kazan (sadece native'de ve düşük kredide) */}
      <WatchAdButton 
        userId={user?.id}
        currentCredits={user?.credits || 0}
        variant="prominent"
        showOnlyWhenLow={false}
        onCreditEarned={(earned, total) => {
          console.log(`Earned ${earned} credits, total now: ${total}`);
          // Credits will refresh automatically via subscription hook
        }}
      />

      {/* Web platform notice */}
      {isWeb && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '0.9rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>📱</span>
          <span>
            {i18n.language === 'tr' 
              ? 'Satın alma işlemleri için mobil uygulamayı kullanın'
              : 'Use the mobile app for purchases'}
          </span>
        </div>
      )}

      {/* Credit costs info - YENİ FİYATLANDIRMA */}
      <div className="credit-costs-info" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '12px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        {creditCosts.map((item, i) => (
          <div key={i} style={{ 
            background: 'rgba(255,255,255,0.1)', 
            padding: '8px 12px', 
            borderRadius: '8px',
            fontSize: '0.85rem'
          }}>
            {item.icon} {item.name}: <strong>{item.cost} 💎</strong>
          </div>
        ))}
      </div>

      {/* TEK PLAN: Mystic Başlangıç Paketi */}
      <div 
        className="plan-card"
        style={{
          background: `linear-gradient(135deg, ${starterPlan.color}30, ${starterPlan.color}15)`,
          border: `2px solid ${starterPlan.color}`,
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '400px',
          margin: '0 auto 24px',
          position: 'relative'
        }}
      >
        {/* Popular badge */}
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: starterPlan.color,
          color: 'white',
          padding: '6px 20px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}>
          {i18n.language === 'tr' ? '🌟 Lansman Paketi' : '🌟 Launch Pack'}
        </div>

        <h3 style={{ fontSize: '1.6rem', marginBottom: '8px', marginTop: '8px', color: 'white' }}>
          {i18n.language === 'tr' ? starterPlan.name : starterPlan.name_en}
        </h3>
        
        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '8px', color: '#fbbf24' }}>
          {starterPlan.price} ₺
        </div>
        
        <div style={{ 
          background: `${starterPlan.color}40`, 
          padding: '10px 20px', 
          borderRadius: '10px',
          marginBottom: '20px',
          display: 'inline-block',
          fontSize: '1.1rem'
        }}>
          💎 {starterPlan.credits} {i18n.language === 'tr' ? 'kredi • 30 gün geçerli' : 'credits • Valid 30 days'}
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
          {(i18n.language === 'tr' ? starterPlan.features_tr : starterPlan.features_en).map((feature, i) => (
            <li key={i} style={{ 
              padding: '8px 0', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              fontSize: '1rem',
              color: 'rgba(255,255,255,0.9)'
            }}>
              <span style={{ color: '#22c55e', fontSize: '1.2rem' }}>✓</span>
              {feature}
            </li>
          ))}
        </ul>

        <button 
          className="btn-premium"
          onClick={handlePurchase}
          disabled={loading}
          style={{ 
            width: '100%', 
            padding: '16px 24px',
            fontSize: '1.1rem',
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          {loading ? '...' : (i18n.language === 'tr' ? '🔮 Hemen Satın Al' : '🔮 Buy Now')}
        </button>
      </div>

      <button className="btn-ghost" onClick={handleRestore} disabled={loading}>
        {loading ? '...' : (i18n.language === 'tr' ? '🔄 Satın Alımları Geri Yükle' : '🔄 Restore Purchases')}
      </button>

      {/* Debug Button - for troubleshooting billing issues */}
      {!isWeb && (
        <button 
          onClick={runBillingDebug} 
          disabled={loading}
          style={{
            marginTop: '20px',
            padding: '12px 24px',
            background: 'rgba(245, 158, 11, 0.2)',
            border: '1px solid rgba(245, 158, 11, 0.5)',
            borderRadius: '8px',
            color: '#fbbf24',
            fontSize: '0.9rem',
            cursor: 'pointer'
          }}
        >
          {loading ? '...' : '🔍 Billing Debug'}
        </button>
      )}

      {/* Debug Info Display */}
      {showDebug && debugInfo && (
        <div style={{
          marginTop: '20px',
          background: 'rgba(0,0,0,0.5)',
          border: '1px solid rgba(245, 158, 11, 0.5)',
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'left',
          fontSize: '0.8rem',
          fontFamily: 'monospace',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          <div style={{ color: '#fbbf24', marginBottom: '8px', fontWeight: 'bold' }}>
            🔍 BILLING DEBUG INFO
          </div>
          <div style={{ color: 'white' }}>
            <div>Platform: {debugInfo.platform}</div>
            <div>Is Native: {String(debugInfo.isNative)}</div>
            <div>Initialized: {String(debugInfo.isInitialized)}</div>
            <div style={{ color: '#22c55e', fontWeight: 'bold' }}>
              SKU: {debugInfo.configuredSKU}
            </div>
            <div>Type: {debugInfo.productType}</div>
            <hr style={{ borderColor: 'rgba(255,255,255,0.2)', margin: '8px 0' }} />
            <div style={{ color: '#60a5fa' }}>Query Results:</div>
            <div>Products Found: {debugInfo.queryResults?.productCount || 0}</div>
            <div>Queried SKU: {debugInfo.queryResults?.queriedSKU}</div>
            {debugInfo.errors.length > 0 && (
              <div style={{ color: '#ef4444', marginTop: '8px' }}>
                Errors: {debugInfo.errors.join(', ')}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowDebug(false)}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Kapat
          </button>
        </div>
      )}

      <p style={{ marginTop: '16px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
        📱 {i18n.language === 'tr' ? 'Google Play üzerinden güvenli ödeme' : 'Secure payment via Google Play'}
      </p>
    </motion.div>
  );
};

// ============ PALM ROUTE GUARDS ============
// Phase 2.5: Proper auth/access state handling

/**
 * PalmRouteGuard - Handles auth & access states for /palm and /palm/new
 */
function PalmRouteGuard({ authInitializing, user, setUser, children }) {
  const [accessState, setAccessState] = useState('loading'); // loading | granted | denied | coming_soon
  const [accessData, setAccessData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check access when user is available
  useEffect(() => {
    if (authInitializing) return;
    if (!user) {
      // Store intended URL for post-login redirect
      sessionStorage.setItem('intendedUrl', location.pathname);
      return;
    }
    
    checkAccess();
  }, [authInitializing, user]);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/palm/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAccessData(response.data);
      
      if (response.data.has_access) {
        setAccessState('granted');
      } else if (response.data.show_coming_soon) {
        setAccessState('coming_soon');
      } else {
        setAccessState('denied');
      }
    } catch (error) {
      console.error('Palm access check failed:', error);
      setAccessState('denied');
    }
  };

  // 1. Auth initializing - show loading
  if (authInitializing) {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <span style={{ fontSize: '48px' }}>✋</span>
          </motion.div>
          <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>Kimlik doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  // 2. Unauthenticated - show login prompt
  if (!user) {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-login-required">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>🔐</span>
          <h2 style={{ margin: '0 0 12px', fontSize: '24px' }}>Giriş Yapın</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Palm Oracle&apos;a erişmek için giriş yapmanız gerekiyor.</p>
          <button
            className="palm-btn palm-btn-primary"
            onClick={() => navigate('/?showAuth=login')}
            style={{ marginBottom: '12px', width: '200px' }}
          >
            Giriş Yap
          </button>
          <button
            className="palm-btn palm-btn-ghost"
            onClick={() => navigate('/')}
            style={{ width: '200px' }}
          >
            ← Ana Sayfa
          </button>
        </div>
      </div>
    );
  }

  // 3. Access loading
  if (accessState === 'loading') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <span style={{ fontSize: '48px' }}>✋</span>
          </motion.div>
          <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>Erişim kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  // 4. Coming soon
  if (accessState === 'coming_soon') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-coming-soon">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <span style={{ fontSize: '80px', display: 'block', marginBottom: '24px', filter: 'drop-shadow(0 0 30px rgba(168,85,247,0.5))' }}>✋</span>
          </motion.div>
          <h1 style={{ margin: '0 0 16px', fontSize: '32px' }}>Palm Oracle</h1>
          <p style={{ margin: '0 0 32px', color: 'rgba(255,255,255,0.7)', maxWidth: '300px' }}>
            Yakında sizlerle! AI destekli el falı deneyimi için hazırlanıyoruz.
          </p>
          <button className="palm-btn palm-btn-secondary" onClick={() => navigate('/')}>
            ← Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  // 5. Access denied - show upgrade
  if (accessState === 'denied') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-access-denied">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>🔒</span>
          <h2 style={{ margin: '0 0 12px', fontSize: '24px' }}>Premium Özellik</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Palm Oracle&apos;a erişmek için Premium üyelik gereklidir.</p>
          <button
            className="palm-btn palm-btn-primary"
            onClick={() => navigate('/?upgrade=palm')}
            style={{ marginBottom: '12px', width: '200px' }}
          >
            Premium&apos;a Geç
          </button>
          <button
            className="palm-btn palm-btn-ghost"
            onClick={() => navigate('/')}
            style={{ width: '200px' }}
          >
            ← Ana Sayfa
          </button>
        </div>
      </div>
    );
  }

  // 6. Access granted - render children with user credits
  return React.cloneElement(children, { 
    credits: user.credits,
    user: user
  });
}

/**
 * PalmReadingRouteGuard - Handles reading ownership verification
 */
function PalmReadingRouteGuard({ authInitializing, user, setUser }) {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [accessState, setAccessState] = useState('loading');
  const [readingState, setReadingState] = useState('loading'); // loading | ready | not_found | forbidden | failed
  const [readingData, setReadingData] = useState(null);

  // Check access when user is available
  useEffect(() => {
    if (authInitializing) return;
    if (!user) {
      sessionStorage.setItem('intendedUrl', location.pathname);
      return;
    }
    
    checkAccess();
  }, [authInitializing, user]);

  // Check reading when access granted
  useEffect(() => {
    if (accessState === 'granted' && readingId) {
      checkReading();
    }
  }, [accessState, readingId]);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/palm/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_access) {
        setAccessState('granted');
      } else {
        setAccessState('denied');
      }
    } catch (error) {
      setAccessState('denied');
    }
  };

  const checkReading = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/palm/readings/${readingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReadingData(response.data);
      setReadingState('ready');
    } catch (error) {
      if (error.response?.status === 404) {
        setReadingState('not_found');
      } else if (error.response?.status === 403) {
        setReadingState('forbidden');
      } else {
        setReadingState('failed');
      }
    }
  };

  // Auth initializing
  if (authInitializing) {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <span style={{ fontSize: '48px' }}>✋</span>
          </motion.div>
          <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>Kimlik doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  // Unauthenticated
  if (!user) {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-reading-login-required">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>🔐</span>
          <h2 style={{ margin: '0 0 12px' }}>Giriş Yapın</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Bu okumayı görmek için giriş yapmanız gerekiyor.</p>
          <button className="palm-btn palm-btn-primary" onClick={() => navigate('/?showAuth=login')} style={{ marginBottom: '12px' }}>
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  // Access loading
  if (accessState === 'loading') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <span style={{ fontSize: '48px' }}>✋</span>
          </motion.div>
          <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>Erişim kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (accessState === 'denied') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-reading-access-denied">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>🔒</span>
          <h2 style={{ margin: '0 0 12px' }}>Premium Özellik</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Bu okumaya erişmek için Premium üyelik gereklidir.</p>
          <button className="palm-btn palm-btn-primary" onClick={() => navigate('/?upgrade=palm')}>Premium&apos;a Geç</button>
        </div>
      </div>
    );
  }

  // Reading loading
  if (readingState === 'loading') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <span style={{ fontSize: '48px' }}>✋</span>
          </motion.div>
          <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>Okuma yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Reading not found
  if (readingState === 'not_found') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-reading-not-found">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>🔍</span>
          <h2 style={{ margin: '0 0 12px' }}>Okuma Bulunamadı</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Bu el falı okuması bulunamadı veya silinmiş olabilir.</p>
          <button className="palm-btn palm-btn-primary" onClick={() => navigate('/palm')}>Palm Oracle&apos;a Dön</button>
        </div>
      </div>
    );
  }

  // Reading forbidden (not owner)
  if (readingState === 'forbidden') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-reading-forbidden">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>🚫</span>
          <h2 style={{ margin: '0 0 12px' }}>Erişim Reddedildi</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Bu el falı okumasına erişim yetkiniz yok.</p>
          <button className="palm-btn palm-btn-primary" onClick={() => navigate('/palm')}>Palm Oracle&apos;a Dön</button>
        </div>
      </div>
    );
  }

  // Reading failed
  if (readingState === 'failed') {
    return (
      <div className="palm-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} data-testid="palm-reading-failed">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.5 }}>⚠️</span>
          <h2 style={{ margin: '0 0 12px' }}>Bir Hata Oluştu</h2>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)' }}>Okuma yüklenirken bir hata oluştu.</p>
          <button className="palm-btn palm-btn-primary" onClick={() => window.location.reload()}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  // Reading ready - render PalmReadingPage
  return (
    <PalmReadingPage 
      token={localStorage.getItem('token')} 
      credits={user.credits}
      initialReadingData={readingData}
      onCreditsUpdate={() => {
        const token = localStorage.getItem('token');
        if (token) {
          axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => setUser(res.data)).catch(() => {});
        }
      }}
    />
  );
}

// ============ MAIN APP ============

function App() {
  const [user, setUser] = useState(null);
  const [authInitializing, setAuthInitializing] = useState(true); // Phase 2.5: Auth loading state
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Initialize analytics on app startup
  useEffect(() => {
    initializeAnalytics().catch(err => {
      // Fail silently - analytics should never break the app
      console.debug('Analytics init note:', err.message);
    });
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          // First, set user from localStorage for faster UX
          setUser(JSON.parse(savedUser));
          
          // Then validate with backend
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data) {
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          }
        } catch (error) {
          // Token invalid - clear auth
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
          // Network error - keep using saved user
        }
      }
      
      // Auth initialization complete
      setAuthInitializing(false);
    };
    
    initAuth();

    // Check for payment success/cancel in URL
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (payment === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      setTimeout(() => setPaymentStatus(null), 5000);
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${API_URL}/api/payments/session-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.is_premium) {
        setPaymentStatus('success');
        // Update user state
        const updatedUser = { ...user, premium: true };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        
        setTimeout(() => setPaymentStatus(null), 5000);
      }
    } catch (err) {
      console.error('Payment status check failed:', err);
    }
  };

  const handleShowAuth = (mode) => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Hero onShowAuth={handleShowAuth} onLogin={setUser} />
              )
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/growth-hub" element={<GrowthHubLayout />} />
          <Route path="/admin/ads-analytics" element={<AdsAnalytics />} />
          <Route path="/admin/country-analytics" element={<CountryAnalytics />} />
          <Route path="/gift/:slug" element={<GiftLanding />} />
          <Route path="/gift/redeem/:giftCode" element={<GiftRedeemPage />} />
          <Route path="/auth/callback" element={<AuthCallback onSuccess={setUser} />} />
          
          {/* Demo Routes */}
          <Route path="/demo/espressolab-finder" element={<EspressolabFinderDemo />} />
          
          {/* Oracle Routes - Plan bazlı erişim: Premium/Pro/Elite/Admin */}
          <Route 
            path="/oracle" 
            element={
              user ? (
                // Check if user has Oracle access (Premium, Pro, Elite, or Admin)
                (() => {
                  const adminEmails = ['admin@mysticai.com', 'hak.dalmis@gmail.com', 'hakandalmis@paracini.com'];
                  const isAdmin = adminEmails.includes(user.email);
                  const isPremium = ['starter', 'premium', 'pro', 'elite'].includes(user.subscription_type);
                  
                  if (isAdmin || isPremium) {
                    return <OracleLandingPage credits={user.credits} />;
                  } else {
                    // Free user - redirect to premium page
                    return <Navigate to="/?upgrade=oracle" replace />;
                  }
                })()
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/oracle/new" 
            element={
              user ? (
                (() => {
                  const adminEmails = ['admin@mysticai.com', 'hak.dalmis@gmail.com', 'hakandalmis@paracini.com'];
                  const isAdmin = adminEmails.includes(user.email);
                  const isPremium = ['starter', 'premium', 'pro', 'elite'].includes(user.subscription_type);
                  
                  if (isAdmin || isPremium) {
                    return (
                      <OracleNewPage 
                        token={localStorage.getItem('token')} 
                        credits={user.credits}
                        onCreditsUpdate={() => {
                          const token = localStorage.getItem('token');
                          if (token) {
                            axios.get(`${API_URL}/api/auth/me`, {
                              headers: { Authorization: `Bearer ${token}` }
                            }).then(res => setUser(res.data)).catch(() => {});
                          }
                        }}
                      />
                    );
                  } else {
                    return <Navigate to="/?upgrade=oracle" replace />;
                  }
                })()
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/oracle/reading/:readingId" 
            element={
              user ? (
                (() => {
                  const adminEmails = ['admin@mysticai.com', 'hak.dalmis@gmail.com', 'hakandalmis@paracini.com'];
                  const isAdmin = adminEmails.includes(user.email);
                  const isPremium = ['starter', 'premium', 'pro', 'elite'].includes(user.subscription_type);
                  
                  if (isAdmin || isPremium) {
                    return (
                      <OracleReadingPage 
                        token={localStorage.getItem('token')} 
                        credits={user.credits}
                        onCreditsUpdate={() => {
                          const token = localStorage.getItem('token');
                          if (token) {
                            axios.get(`${API_URL}/api/auth/me`, {
                              headers: { Authorization: `Bearer ${token}` }
                            }).then(res => setUser(res.data)).catch(() => {});
                          }
                        }}
                      />
                    );
                  } else {
                    return <Navigate to="/?upgrade=oracle" replace />;
                  }
                })()
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          
          {/* Palm Oracle Routes - AI El Falı (v2.5.0) */}
          {/* Phase 2.5: Proper auth/access state handling */}
          <Route 
            path="/palm" 
            element={
              <PalmRouteGuard 
                authInitializing={authInitializing}
                user={user}
                setUser={setUser}
              >
                <PalmLandingPage credits={user?.credits || 0} user={user} />
              </PalmRouteGuard>
            } 
          />
          <Route 
            path="/palm/new" 
            element={
              <PalmRouteGuard 
                authInitializing={authInitializing}
                user={user}
                setUser={setUser}
              >
                <PalmNewPage 
                  token={localStorage.getItem('token')}
                  credits={user?.credits || 0}
                  user={user}
                  onCreditsUpdate={() => {
                    const token = localStorage.getItem('token');
                    if (token) {
                      axios.get(`${API_URL}/api/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                      }).then(res => setUser(res.data)).catch(() => {});
                    }
                  }}
                />
              </PalmRouteGuard>
            } 
          />
          <Route 
            path="/palm/reading/:readingId" 
            element={
              <PalmReadingRouteGuard 
                authInitializing={authInitializing}
                user={user}
                setUser={setUser}
              />
            } 
          />
        </Routes>
      </BrowserRouter>

      <AnimatePresence mode="wait">
        {showAuth && (
          <AuthModal
            key="auth-modal"
            mode={authMode}
            onClose={() => setShowAuth(false)}
            onSuccess={setUser}
          />
        )}
      </AnimatePresence>

      {/* Payment Status Notifications */}
      <AnimatePresence>
        {paymentStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="payment-notification success"
          >
            🎉 Payment successful! You're now Premium!
          </motion.div>
        )}
        {paymentStatus === 'cancelled' && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="payment-notification cancelled"
          >
            ❌ Payment cancelled. You can upgrade anytime!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;